import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'webfolio_cart_v1';
const DEFAULT_STOCK = 20;

function safeParseCartItems(value) {
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x) => x && typeof x === 'object')
      .map((x) => ({
        id: String(x.id ?? ''),
        name: String(x.name ?? ''),
        price: Number(x.price ?? 0),
        qty: Math.max(1, Number(x.qty ?? 1)),
        slug: x.slug ? String(x.slug) : undefined,
        image: x.image ? String(x.image) : undefined,
        maxQty: (() => {
          const parsedMax =
            x.maxQty === undefined || x.maxQty === null ? undefined : Number(x.maxQty);
          const id = String(x.id ?? '');
          // Migration: old saved items had maxQty=1; allow increasing now.
          if (id.startsWith('shop:') && (parsedMax === undefined || parsedMax <= 1)) {
            return DEFAULT_STOCK;
          }
          return parsedMax;
        })(),
      }))
      .filter((x) => x.id && x.name && Number.isFinite(x.price) && Number.isFinite(x.qty));
  } catch (_) {
    return [];
  }
}

function clampQty(qty, maxQty) {
  const n = Number(qty);
  const base = Number.isFinite(n) ? n : 1;
  const minned = Math.max(1, base);
  const m = Number(maxQty);
  if (maxQty === undefined || maxQty === null) return minned;
  if (!Number.isFinite(m)) return minned;
  return Math.min(minned, m);
}

function reducer(state, action) {
  switch (action.type) {
    case 'add': {
      const { item } = action;
      const existing = state.items.find((x) => x.id === item.id);
      const items = existing
        ? state.items.map((x) =>
            x.id === item.id
              ? {
                  ...x,
                  ...item,
                  qty: clampQty(x.qty + item.qty, item.maxQty ?? x.maxQty),
                }
              : x
          )
        : [
            ...state.items,
            {
              ...item,
              qty: clampQty(item.qty, item.maxQty),
            },
          ];
      return { ...state, items };
    }
    case 'remove': {
      return { ...state, items: state.items.filter((x) => x.id !== action.id) };
    }
    case 'setQty': {
      const items = state.items
        .map((x) =>
          x.id === action.id ? { ...x, qty: clampQty(action.qty, x.maxQty) } : x
        )
        .filter((x) => x.qty > 0);
      return { ...state, items };
    }
    case 'clear':
      return { ...state, items: [] };
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(
    reducer,
    { items: [] },
    () => {
      if (typeof window === 'undefined') return { items: [] };
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return { items: [] };
      return { items: safeParseCartItems(raw) };
    }
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
    } catch (_) {}
  }, [state.items]);

  const value = useMemo(() => {
    const itemCount = state.items.reduce((sum, x) => sum + x.qty, 0);
    const subtotal = state.items.reduce((sum, x) => sum + x.qty * x.price, 0);
    return {
      items: state.items,
      itemCount,
      subtotal,
      addItem: (item) => dispatch({ type: 'add', item }),
      removeItem: (id) => dispatch({ type: 'remove', id }),
      setQty: (id, qty) => dispatch({ type: 'setQty', id, qty }),
      clear: () => dispatch({ type: 'clear' }),
    };
  }, [state.items]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}

